// invite.effects.ts
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { catchError, map, switchMap } from 'rxjs';
import { HttpOperation } from '../../enums/http-operation';
import {
  AcceptInvitationResponse,
  ResolveInvitationResponse,
} from '../../models/invitation/inviation.model';
import { ShiveRoutes } from '../../routing/segments/route-segments';
import { EndpointService } from '../../services/endpoints/endpoint.service';
import { HttpErrorService } from '../../services/http-error.service';
import * as fromInvitationActions from './invitation.actions';

@Injectable()
export class InvitationEffects {
  createInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromInvitationActions.createInvitation),
      map((payload) => payload.email),
      switchMap((email) => {
        const endpoint = this.endpointService.getInvitationSlug(
          HttpOperation.Post,
          ShiveRoutes.Create,
        );

        return this.http.post<any>(endpoint, { email }).pipe(
          map(() => fromInvitationActions.createInvitationSuccess()),
          catchError((error) =>
            this.httpErrorService.handleError(
              'Invitation',
              fromInvitationActions.createInvitation,
              fromInvitationActions,
              error,
            ),
          ),
        );
      }),
    ),
  );

  resolveInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromInvitationActions.resolveInvitation),
      switchMap(({ token }) => {
        const endpoint = this.endpointService.getInvitationSlug(
          HttpOperation.Get,
          ShiveRoutes.Resolve,
        );
        const params = new HttpParams().set('token', token);

        return this.http.get<any>(endpoint, { params }).pipe(
          map((json: unknown) => {
            const resolveSuccess = plainToInstance(ResolveInvitationResponse, json);
            return fromInvitationActions.resolveInvitationSuccess({ payload: resolveSuccess });
          }),
          catchError((error) =>
            this.httpErrorService.handleError(
              'Invitation',
              fromInvitationActions.resolveInvitation,
              fromInvitationActions,
              error,
            ),
          ),
        );
      }),
    ),
  );

  acceptInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromInvitationActions.acceptInvitation),
      switchMap(({ payload }) => {
        const endpoint = this.endpointService.getInvitationSlug(
          HttpOperation.Post,
          ShiveRoutes.Accept,
        );

        const data = instanceToPlain(payload);

        return this.http.post<any>(endpoint, data).pipe(
          map((json: unknown) => {
            const acceptInvitationSuccess = plainToInstance(AcceptInvitationResponse, json);
            return fromInvitationActions.acceptInvitationSuccess({
              payload: acceptInvitationSuccess,
            });
          }),
          catchError((error) =>
            this.httpErrorService.handleError(
              'Invitation',
              fromInvitationActions.acceptInvitation,
              fromInvitationActions,
              error,
            ),
          ),
        );
      }),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly http: HttpClient,
    private readonly endpointService: EndpointService,
    private readonly httpErrorService: HttpErrorService,
  ) {}
}
